import React, { useEffect, useMemo, useState } from 'react'
import { inputStyles } from './shared/FormStyles'
import BackgroundImageGallery from './shared/BackgroundImageGallery'
import { toast } from 'sonner'
import { Vibrant } from 'node-vibrant/browser'
import logger from '../../../utils/logger'
import debounce from 'lodash.debounce'
import ColorPicker from '../../../components/color-picker/ColorPicker'

const PlanUpgradeMessage = React.memo(() => (
  <div className="mt-4 p-4 bg-gray-50 border rounded-lg">
    <div className="flex items-center gap-3">
      <span className="text-xl">🔒</span>
      <div>
        <h4 className="font-medium text-gray-900">Premium Feature</h4>
        <p className="text-sm text-gray-600">
          Custom Background uploads are available in Premium or Enterprise plans
        </p>
        <button
          className="mt-2 px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full hover:bg-blue-600 transition-colors"
          onClick={() => {
            logger.log('Navigate to upgrade plan')
          }}
        >
          Upgrade Now
        </button>
      </div>
    </div>
  </div>
))

const BackgroundForm = ({ background, onChange, onFileChange, plan }) => {
  const [palette, setPalette] = useState(null)
  const [selectedColorForWalletCard, setSelectedColorForWalletCard] = useState(null)
  const [activeTab, setActiveTab] = useState('color')
  const [isLoading, setIsLoading] = useState(false)

  logger.log('background =====> ', background)

  // when the component render for the first time call the useEffect and check if the background type is image
  // if the background type is image then create the pallete and set the palette
  useEffect(() => {
    if (background?.type === 'image') {
      const imageDataUrl = background?.value

      const extractPalette = async () => {
        try {
          const paletteResult = await Vibrant.from(imageDataUrl).getPalette()
          setPalette(paletteResult)

          // if walletColor is present
          if (background.selectedColorForWalletCard) {
            setSelectedColorForWalletCard(background.selectedColorForWalletCard)
          } else {
            // Reset selected color to match the new image palette
            const fallback = paletteResult.Muted?.hex || paletteResult.Vibrant?.hex || '#000000'
            setSelectedColorForWalletCard(fallback)

            // Update parent with new image + color
            onChange({
              type: 'image',
              value: imageDataUrl,
              walletCardBackgroundColor: fallback,
            })
          }
        } catch (error) {
          console.error('Error extracting palette:', error)
        }
      }

      extractPalette()
    }
  }, [])

  // Debounced function only for notifying parent
  const debouncedNotifyParent = useMemo(
    () =>
      debounce((newColor) => {
        onChange({
          type: 'image',
          value: background?.value,
          walletCardBackgroundColor: newColor,
        })
      }, 300),
    [onChange, background?.value]
  )

  useEffect(() => {
    return () => {
      debouncedNotifyParent.cancel()
    }
  }, [debouncedNotifyParent])

  // const handleColorChange = (color) => {
  //   // TODO: we have to send the wallet color aslo with it same as the color
  //   onChange({ type: 'color', value: color, walletCardBackgroundColor: color })
  //   onFileChange(null) // Clear any stored background image file
  // }

  const validateImage = (file) => {
    const maxSize = 20 * 1024 * 1024 // 20MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    const allowedExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']

    if (!file) {
      toast.error('File is required')
      return false
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error(`Only ${allowedExtensions.join(', ')} files are allowed`)
      return false
    }

    if (file.size >= maxSize) {
      toast.error(`File size must be less than ${maxSize / (1024 * 1024)}MB`)
      return false
    }

    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Invalid file extension. Allowed: ${allowedExtensions.join(', ')}`)
      return false
    }

    return true
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file && validateImage(file)) {
      onFileChange(file)
      const reader = new FileReader()
      reader.onloadend = async () => {
        const imageDataUrl = reader.result

        try {
          const paletteResult = await Vibrant.from(imageDataUrl).getPalette()

          setPalette(paletteResult)

          // Auto-select a fallback (like Vibrant)
          const fallback = paletteResult.Muted?.hex || paletteResult.Vibrant?.hex || '#000000'
          setSelectedColorForWalletCard(fallback)

          onChange({
            type: 'image',
            value: imageDataUrl,
            walletCardBackgroundColor: fallback,
          })
          // here this is the image change method and after that we have to send the wallet color aslo with it
        } catch (error) {
          console.error(error)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const handleGallerySelect = async (imageUrl) => {
    setIsLoading(true)
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const file = new File([blob], 'background.jpg', { type: 'image/jpeg' })

      if (validateImage(file)) {
        onFileChange(file)
        const reader = new FileReader()
        reader.onloadend = async () => {
          const imageDataUrl = reader.result

          try {
            const paletteResult = await Vibrant.from(imageDataUrl).getPalette()

            setPalette(paletteResult)

            // Auto-select a fallback (like Vibrant)
            const fallback = paletteResult.Muted?.hex || paletteResult.Vibrant?.hex || '#000000'
            setSelectedColorForWalletCard(fallback)

            // here this is the image change method and after that we have to send the wallet color aslo with it
            onChange({
              type: 'image',
              value: imageDataUrl,
              walletCardBackgroundColor: fallback,
            })
          } catch (error) {
            console.error(error)
          }
        }
        reader.readAsDataURL(blob)
      }
    } catch (error) {
      console.error('Error processing gallery image:', error)
      toast.error('Failed to process gallery image')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={inputStyles.container}>
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          className={`px-4 py-2 rounded-full text-sm ${
            activeTab === 'color'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => setActiveTab('color')}
        >
          Color
        </button>
        <button
          className={`px-4 py-2 rounded-full text-sm ${
            activeTab === 'image'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => setActiveTab('image')}
        >
          Image
        </button>
      </div>

      {/* Color Section */}
      {activeTab === 'color' && (
        <div>
          <label className="block">
            <span className={inputStyles.label}>Background Color</span>
            <div className="mt-2 flex items-center gap-3">
              <ColorPicker
                value={background.type === 'color' ? background.value : '#ffffff'}
                onChange={(newColor) => {
                  onChange({ type: 'color', value: newColor, walletCardBackgroundColor: newColor })
                  onFileChange(null) // Clear any stored background image file
                }}
              />
              <span className="text-sm text-gray-500">
                {background.type === 'color' ? background.value : '#ffffff'}
              </span>
            </div>
          </label>

          <div className="mt-6">
            <span className={inputStyles.label}>Quick Colors</span>
            <div className="mt-2 grid grid-cols-6 gap-2">
              {['#ffffff', '#fee2e2', '#dbeafe', '#dcfce7', '#dcf217', '#fef9c3'].map((color) => (
                <button
                  key={color}
                  className="w-8 h-8 rounded-full border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    onChange({ type: 'color', value: color, walletCardBackgroundColor: color })
                    onFileChange(null) // Clear any stored background image file
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Image Section */}
      {activeTab === 'image' && (
        <div className="space-y-6">
          {plan !== 'basic' ? (
            <div>
              <span className={inputStyles.label}>Upload Custom Image</span>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                  disabled={isLoading}
                />

                <div className="px-4 py-2 border rounded-md bg-white text-gray-600 hover:bg-gray-50">
                  Choose Image
                </div>
              </div>
              {palette && (
                <div className="mt-6">
                  <span className={inputStyles.label}>
                    Choose a background color for the wallet card
                  </span>

                  {/* Palette Swatches */}
                  <div className="mt-2 flex flex-wrap gap-3">
                    {Object.entries(palette).map(([key, swatch]) =>
                      swatch ? (
                        <button
                          key={key}
                          className={`w-12 h-12 rounded-md border-2 transition cursor-pointer ${
                            selectedColorForWalletCard === swatch.hex
                              ? 'border-blue-500 scale-110'
                              : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: swatch.hex }}
                          onClick={() => {
                            setSelectedColorForWalletCard(swatch.hex)

                            // call the onChange method on every color change for update the wallet color
                            onChange({
                              type: 'image',
                              value: background?.value, // keep the same image
                              walletCardBackgroundColor: swatch.hex,
                            })
                          }}
                          title={key}
                        />
                      ) : null
                    )}
                  </div>

                  {/* Selected Color Info */}
                  {selectedColorForWalletCard && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected:{' '}
                      <span style={{ color: selectedColorForWalletCard }}>
                        {selectedColorForWalletCard}
                      </span>
                    </p>
                  )}

                  {/* Custom Color Picker */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Or pick a custom color
                    </label>
                    <ColorPicker
                      value={selectedColorForWalletCard || '#000000'}
                      onChange={(newColor) => {
                        setSelectedColorForWalletCard(newColor)
                        onChange({
                          type: 'image',
                          value: background?.value, // keep the same image
                          walletCardBackgroundColor: newColor,
                        })
                      }}
                    />
                  </div>
                </div>
              )}

              {/* File Requirements Card */}
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-red-500 text-xl">⚠️</span>
                  <div>
                    <h4 className="font-medium text-red-800 mb-1">File Limitations</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li className="flex items-center space-x-2">
                        <span>📁</span>
                        <span>Supported formats: JPEG, PNG, GIF, WebP, SVG</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <span>📊</span>
                        <span>Maximum size: 20MB</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Best Practices Tip Card */}
              <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-amber-500 text-xl">💡</span>
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">Pro Tip</h4>
                    <p className="text-sm text-amber-700">
                      For best visual results, use images of 700 x 350 pixels dimensions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <PlanUpgradeMessage />
          )}

          <hr />
          <div>
            <span className={inputStyles.label}>Or choose from gallery</span>
            <div className="mt-2 relative">
              {isLoading && (
                <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              )}
              <BackgroundImageGallery onSelect={handleGallerySelect} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BackgroundForm
